'use client';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { KeyboardEvent, MouseEvent } from 'react';

import { AbgTypography, CloseIcon, mergeSx } from '@abg/kit-base';

import { useAnalyticsGeneralEngagement } from '../../../shared/analytics/hooks/use-analytics-general-engagement';
import { useTranslationContext } from '../../../shared/context';
import { AncillariesI18nKeys } from '../../../shared/types';
import { sanitizeHtml } from '../../../shared/utils';
import { AemImage } from '../../AemImage';

import {
  closeIconStyling,
  dialogActionsStyling,
  dialogButtonStyling,
  dialogContainerStyling,
  dialogContentStyling,
  dialogHeaderStyling,
  dialogSubtitleStyling,
  dialogTitleStyling,
  imageContainerStyling,
  itemContainerStyling,
  itemHeaderStyling,
  itemIconContainerStyling,
  itemIconStyling,
  itemLabelStyling,
  itemSubtitleStyling,
  itemTitleStyling,
} from './IncludedAncillariesDialog.styling';
import { IncludedAncillariesDialogProps } from './types';

export const IncludedAncillariesDialog = ({
  styleParts = {},
  isOpen,
  config,
  includedItems,
  ...props
}: IncludedAncillariesDialogProps) => {
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();
  const theme = useTheme();
  const isMobileLandscape = useMediaQuery(
    `${theme.breakpoints.down('md')} and (orientation: landscape)`,
  );

  const { emitEvent: emitGeneralEngagementEvent } =
    useAnalyticsGeneralEngagement();

  const { image, title, subtitle } = config;

  const onClose = (
    event: MouseEvent<HTMLOrSVGElement> | KeyboardEvent<SVGSVGElement>,
  ) => {
    emitGeneralEngagementEvent(
      event.currentTarget as Element,
      'IncludedAncillariesDialog',
    );
    props.onClose();
  };

  return (
    <Dialog
      open={isOpen}
      aria-labelledby="included-ancillaries-dialog-title"
      aria-describedby="included-ancillaries-dialog-subtitle"
      sx={mergeSx(styleParts.dialogContainer, dialogContainerStyling)}
      slotProps={{
        paper: {
          // @ts-expect-error TS2353
          'data-testid': 'included-ancillaries-dialog',
        },
      }}
    >
      <DialogTitle component="div" sx={dialogHeaderStyling}>
        <CloseIcon
          sx={closeIconStyling}
          stroke={image.src ? 'white' : 'black'}
          data-testid="included-ancillaries-dialog-close-btn"
          onClick={onClose}
          onKeyUp={(event) => {
            if (event.key === 'Enter') {
              onClose(event);
            }
          }}
          tabIndex={0}
          role="button"
          aria-hidden="false"
          aria-label={t('global.button.close.label') || 'Close'}
        />

        {!!image?.src && !isMobileLandscape && (
          <Box sx={imageContainerStyling}>
            <AemImage
              src={image.src}
              alt={image.alt}
              fill
              style={{ objectFit: 'contain' }}
              data-testid="included-ancillaries-dialog-image"
              aria-hidden="true"
            />
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={dialogContentStyling} tabIndex={0}>
        <AbgTypography
          sx={mergeSx(dialogTitleStyling, styleParts.dialogTitle)}
          data-testid="included-ancillaries-dialog-title"
          id="included-ancillaries-dialog-title"
        >
          {title}
        </AbgTypography>

        <AbgTypography
          sx={mergeSx(dialogSubtitleStyling, styleParts.dialogSubtitle)}
          data-testid="included-ancillaries-dialog-subtitle"
          id="included-ancillaries-dialog-subtitle"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(subtitle || ''),
          }}
        />

        {includedItems.map((item) => (
          <Box
            key={item.id}
            sx={mergeSx(itemContainerStyling, styleParts.itemContainer)}
            data-testid="included-ancillaries-dialog-item"
          >
            <Box sx={itemHeaderStyling}>
              <Box
                sx={itemIconContainerStyling}
                data-testid="included-ancillaries-dialog-item-icon"
              >
                {!!item.iconPath && (
                  <Box sx={itemIconStyling}>
                    <AemImage
                      src={item.iconPath}
                      alt={item.title}
                      width={17}
                      height={17}
                      style={{
                        objectFit: 'cover',
                        maxHeight: 17,
                        maxWidth: 17,
                      }}
                      aria-hidden="true"
                    />
                  </Box>
                )}
              </Box>
              <AbgTypography
                sx={mergeSx(itemTitleStyling, styleParts.itemTitle)}
                data-testid="included-ancillaries-dialog-item-title"
              >
                {item.title}
              </AbgTypography>

              <AbgTypography
                sx={mergeSx(itemLabelStyling, styleParts.itemLabel)}
                data-testid="included-ancillaries-dialog-item-label"
              >
                {item.label ?? t(i18nKeys.includedInPlanLabel)}
              </AbgTypography>
            </Box>
            <AbgTypography
              sx={mergeSx(itemSubtitleStyling, styleParts.itemDescription)}
              data-testid="included-ancillaries-dialog-item-description"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(item.description || ''),
              }}
            />
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={dialogActionsStyling}>
        <Button
          data-testid="included-ancillaries-dialog-btn"
          sx={mergeSx(dialogButtonStyling, styleParts.dialogButton)}
          onClick={onClose}
        >
          {t(i18nKeys.exploreAncillariesLabel)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
